"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MutableSet = void 0;
const MutableObject_1 = require("../model/MutableObject");
const MutationOp_1 = require("../model/MutationOp");
const HashedSet_1 = require("../model/HashedSet");
const Types_1 = require("./Types");
const logging_1 = require("../../util/logging");
// a simple mutable set with a single writer
class MutableSetOp extends MutationOp_1.MutationOp {
    constructor(target) {
        super(target);
        if (target !== undefined) {
            let author = target.getAuthor();
            if (author !== undefined) {
                this.setAuthor(author);
            }
        }
    }
    init() {
    }
    async validate(references) {
        var _a;
        if (!await super.validate(references)) {
            return false;
        }
        if (!(this.getTarget() instanceof MutableSet)) {
            return false;
            //throw new Error('MutableSetOp.target must be a MutableSet, got a ' + this.getTarget().getClassName() + ' instead.');
        }
        if (this.getTarget().getAuthor() !== undefined && !((_a = this.getTarget().getAuthor()) === null || _a === void 0 ? void 0 : _a.equals(this.getAuthor()))) {
            return false;
            //throw new Error('MutableSetOp has author ' + this.getAuthor()?.hash() + ' but points to a target authored by ' + this.getTarget().getAuthor()?.hash() + '.');
        }
        return true;
    }
}
class MutableSetAddOp extends MutableSetOp {
    constructor(target, element) {
        super(target);
        if (element !== undefined) {
            this.element = element;
            this.setRandomId();
        }
    }
    getClassName() {
        return MutableSetAddOp.className;
    }
    init() {
        super.init();
    }
    async validate(references) {
        if (!await super.validate(references)) {
            return false;
        }
        const constraints = this.getTarget().typeConstraints;
        if (!Types_1.Types.satisfies(this.element, constraints)) {
            return false;
            //throw new Error('MutableSetAddOp contains a value with an unexpected type.')
        }
        return true;
    }
}
MutableSetAddOp.className = 'hhs/v0/MutableSetAddOp';
MutableSetAddOp.registerClass(MutableSetAddOp.className, MutableSetAddOp);
class MutableSetDeleteOp extends MutableSetOp {
    constructor(target, elementHash, addOps) {
        super(target);
        this.elementHash = elementHash;
        if (addOps !== undefined) {
            this.deletedOps = new HashedSet_1.HashedSet();
            for (const addOp of addOps) {
                if (addOp.className !== MutableSetAddOp.className) {
                    throw new Error('Trying to create a delete op referencing an op that is not an addition op.');
                }
                this.deletedOps.add(addOp);
            }
        }
    }
    // need a valid() function, that is called only when an object is NEW and we don't yet
    // trust its integrity. init() will be called every time it is loaded (after all the
    // fields have been filled in, either by the constructor or by the deliteralization
    // mechanism, and after valid, if it is untrusted)
    // valid needs all the references also, already validated, to do its checks.
    // (all this follows from the need to validate deletedOps)
    init() {
        super.init();
    }
    async validate(references) {
        var _a, _b;
        if (!await super.validate(references)) {
            return false;
        }
        if (this.elementHash === undefined) {
            MutableSet.logger.warning('The field elementHash of type MutableSetDeletOp is mandatory.');
            return false;
        }
        if (typeof this.elementHash !== 'string') {
            MutableSet.logger.warning('The field elementHash of type MutebleSetDeleteOp should be a string.');
            return false;
        }
        if (this.deletedOps === undefined) {
            MutableSet.logger.warning('The field deletedOps of type MutableSetDeleteOp is mandatory');
            return false;
        }
        if (!(this.deletedOps instanceof HashedSet_1.HashedSet)) {
            MutableSet.logger.warning('The field deletedOps of type MutableSetDeleteOp should be a HashedSet.');
            return false;
        }
        for (const ref of this.deletedOps.values()) {
            const op = references.get(ref.hash);
            if (op === undefined) {
                MutableSet.logger.warning('Addition op referenced in MutableSet deletion op is missing from references provided for validation.');
            }
            if (!(op instanceof MutableSetAddOp)) {
                MutableSet.logger.warning('Addition op referenced in MutableSet deletion op has the wrong type in the references provided for validation.');
                return false;
            }
            if (!((_a = op.target) === null || _a === void 0 ? void 0 : _a.equals(this.target))) {
                MutableSet.logger.warning('Addition op referenced in MutableSet deletion op points to a different set.');
                return false;
            }
            const addOp = op;
            if (((_b = addOp.element) === null || _b === void 0 ? void 0 : _b.hash()) !== this.elementHash) {
                MutableSet.logger.warning('Addition op referenced in MutableSet deletion op contains an element whose hash does not match the one being deleted.');
                return false;
            }
        }
        return true;
    }
    getClassName() {
        return MutableSetDeleteOp.className;
    }
}
MutableSetDeleteOp.className = 'hhs/v0/MutableSetDeleteOp';
MutableSetDeleteOp.registerClass(MutableSetDeleteOp.className, MutableSetDeleteOp);
class MutableSet extends MutableObject_1.MutableObject {
    constructor() {
        super(MutableSet.opClasses);
        this._logger = MutableSet.logger;
        this.setRandomId();
        this._elements = new Map();
        this._currentAddOpRefs = new Map();
        this._unsavedAppliedOps = new Set();
    }
    init() {
    }
    async validate(references) {
        references;
        return Types_1.Types.isTypeConstraint(this.typeConstraints);
    }
    async add(element) {
        let op = new MutableSetAddOp(this, element);
        await this.applyNewOp(op);
    }
    async delete(element) {
        return await this.deleteByHash(element.hash());
    }
    async deleteByHash(hash) {
        let addOpRefs = this._currentAddOpRefs.get(hash);
        if (addOpRefs !== undefined && addOpRefs.size() > 0) {
            let op = new MutableSetDeleteOp(this, hash, addOpRefs.values());
            await this.applyNewOp(op);
            return true;
        }
        else {
            return false;
        }
    }
    has(element) {
        return this.hasByHash(element.hash());
    }
    hasByHash(hash) {
        return this._elements.get(hash) !== undefined;
    }
    get(hash) {
        return this._elements.get(hash);
    }
    size() {
        return this._elements.size;
    }
    values() {
        return this._elements.values();
    }
    async mutate(op, isNew) {
        let mutated = false;
        if (op instanceof MutableSetAddOp) {
            const addOp = op;
            let hash = op.element.hash();
            if (hash === undefined) {
                throw new Error('Trying to add an element to set, but the element is undefined.');
            }
            let current = this._currentAddOpRefs.get(hash);
            if (current === undefined) {
                current = new HashedSet_1.HashedSet();
                this._currentAddOpRefs.set(hash, current);
            }
            current.add(addOp.createReference());
            this._elements.set(hash, addOp.element);
            if (isNew) {
                this._unsavedAppliedOps.add(op.hash());
                mutated = true;
            }
            else {
                const opHash = addOp.hash();
                if (!this._unsavedAppliedOps.has(opHash)) {
                    mutated = true;
                }
                else {
                    this._unsavedAppliedOps.delete(opHash);
                }
            }
            if (mutated) {
                if (this._addElementCallback !== undefined) {
                    try {
                        this._addElementCallback(addOp.element);
                    }
                    catch (e) {
                        this._logger.warning(() => ('Error calling MutableSet element addition callback on op ' + addOp.hash()));
                    }
                }
            }
        }
        else if (op instanceof MutableSetDeleteOp) {
            const deleteOp = op;
            let hash = deleteOp.elementHash;
            if (hash === undefined) {
                throw new Error('Trying to remove an element from set, but elementHash is undefined.');
            }
            let current = this._currentAddOpRefs.get(hash);
            if (current !== undefined) {
                if (deleteOp.deletedOps !== undefined) {
                    for (const opRef of deleteOp.deletedOps.values()) {
                        current.remove(opRef);
                    }
                }
                if (current.size() === 0) {
                    mutated = true;
                    const deleted = this._elements.get(hash);
                    this._elements.delete(hash);
                    this._currentAddOpRefs.delete(hash);
                    if (this._deleteElementCallback !== undefined) {
                        try {
                            this._deleteElementCallback(deleted);
                        }
                        catch (e) {
                            this._logger.warning(() => ('Error calling MutableSet element deletion callback on op ' + deleteOp.hash()));
                        }
                    }
                }
            }
        }
        else {
            throw new Error("Method not implemented.");
        }
        return mutated;
    }
    onAddition(callback) {
        this._addElementCallback = callback;
    }
    onDeletion(callback) {
        this._deleteElementCallback = callback;
    }
    getClassName() {
        return MutableSet.className;
    }
}
exports.MutableSet = MutableSet;
MutableSet.className = 'hss/v0/MutableSet';
MutableSet.opClasses = [MutableSetAddOp.className, MutableSetDeleteOp.className];
MutableSet.logger = new logging_1.Logger(MutableSet.className, logging_1.LogLevel.INFO);
MutableSet.registerClass(MutableSet.className, MutableSet);
//# sourceMappingURL=MutableSet.js.map